import { useEffect, useRef, useState } from 'react';
import { useToken } from '../auth/useToken';
import { useAccount } from '../auth/useAccount';
import { getStatus, startLink, unlink, type ChronosStatus } from '../lib/chronos';
import { ConfirmDialog } from './ui/Dialog';
import { Icon } from './Icon';
import {
  loadThemePreference,
  saveThemePreference,
  applyThemePreference,
  type ThemePreference,
} from '../state/theme';

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: string }[] = [
  { value: 'system', label: 'System', icon: 'i-theme-system' },
  { value: 'light', label: 'Light', icon: 'i-sun' },
  { value: 'dark', label: 'Dark', icon: 'i-moon' },
];

interface SettingsPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called when the user clicks "Delete my account & history" in Privacy & data.
   * The parent owns the DeleteAccountDialog (it needs the prompts/conversations
   * counts to render). When this is undefined the row hides.
   */
  onOpenDeleteMe?: () => void;
}

/**
 * Phase-7 settings panel. Three sections (Chronos, Appearance, Privacy & data),
 * anchored above the sidebar's footer cog on desktop and slides up as a
 * bottom-sheet on mobile (the CSS in src/index.css does the responsive shape via
 * @media). The trigger button — settings cog — lives in `.side-foot`; this
 * component just renders the floating panel.
 */
export function SettingsPopover({ open, onOpenChange, onOpenDeleteMe }: SettingsPopoverProps) {
  const getToken = useToken();
  const account = useAccount();
  const [status, setStatus] = useState<ChronosStatus | null>(null);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [theme, setTheme] = useState<ThemePreference>(loadThemePreference);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const token = await getToken();
        setStatus(await getStatus(token));
      } catch (err) {
        console.error('chronos/status failed:', err);
      }
    })();
  }, [open, getToken]);

  // Outside-click + Escape close. Same pattern as the previous version; the
  // [role="dialog"] guard prevents the ConfirmDialog (Radix portal) from
  // bubbling and closing the panel while the user is mid-action.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement) || !panelRef.current) return;
      if (panelRef.current.contains(target)) return;
      if (target.closest('[aria-label="Settings"]')) return;
      if (target.closest('[role="dialog"]')) return;
      onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const doUnlink = async () => {
    const token = await getToken();
    await unlink(token);
    setStatus(await getStatus(token));
  };

  // Mirrors ChronosBanner.tsx — same getToken → startLink → redirect flow.
  // The chat-top banner is still the discovery affordance for first-time
  // users; this inline row is the secondary "control" surface for users who
  // dismissed the banner or who landed in Settings first.
  const doLink = async () => {
    setLinkBusy(true);
    try {
      const token = await getToken();
      const { authorizeUrl } = await startLink(token);
      window.location.href = authorizeUrl;
    } catch (err) {
      console.error('Chronos link failed:', err);
      setLinkBusy(false);
    }
  };

  const chooseTheme = (t: ThemePreference) => {
    setTheme(t);
    saveThemePreference(t);
    applyThemePreference(t);
  };

  return (
    <>
      {/* Scrim — only visible on mobile (bottom-sheet variant) per CSS @media. */}
      <div className="settings-sheet-scrim is-open" onClick={() => onOpenChange(false)} />

      <div
        ref={panelRef}
        className="settings-anchor is-open settings-panel"
        role="dialog"
        aria-label="Settings"
      >
        {/* Grabber bar — matches the mock's bottom-sheet affordance.
            Decorative on desktop; functional on mobile (drag-to-dismiss
            hook can attach later). */}
        <div className="settings-sheet__grabber" aria-hidden="true" />
        <div className="settings-panel__head">
          <span>Settings</span>
          <button
            type="button"
            className="settings-panel__close"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
          >
            <Icon name="i-close" size={16} />
          </button>
        </div>

        {/* Chronos — render both link states inline so users who land here
            after dismissing the ChronosBanner can still relink. The banner
            handles first-time discovery; this row is the durable control. */}
        {status?.enabled && (
          <section className="settings-panel__section">
            <div className="settings-panel__label">Chronos</div>
            <div className="settings-panel__row">
              {status.linked ? (
                <>
                  <div className="settings-panel__row-value">
                    Linked as <strong>{account?.name ?? account?.username}</strong>
                    {status.timeZoneName && ` · ${status.timeZoneName}`}
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmUnlink(true)}
                    className="settings-panel__danger"
                  >
                    Unlink
                  </button>
                </>
              ) : status.needsReconnect ? (
                <>
                  <div className="settings-panel__row-value settings-panel__row-value--muted">
                    Connection expired
                  </div>
                  <button
                    type="button"
                    onClick={doLink}
                    disabled={linkBusy}
                    className="settings-panel__primary"
                  >
                    {linkBusy ? 'Reconnecting…' : 'Reconnect'}
                  </button>
                </>
              ) : (
                <>
                  <div className="settings-panel__row-value settings-panel__row-value--muted">
                    Not linked
                  </div>
                  <button
                    type="button"
                    onClick={doLink}
                    disabled={linkBusy}
                    className="settings-panel__primary"
                  >
                    {linkBusy ? 'Linking…' : 'Link Chronos'}
                  </button>
                </>
              )}
            </div>
          </section>
        )}

        {/* Appearance */}
        <section className="settings-panel__section">
          <div className="settings-panel__label">Appearance</div>
          <div className="theme-toggle" role="radiogroup" aria-label="Theme">
            {THEME_OPTIONS.map(({ value, label, icon }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={theme === value}
                className={`theme-toggle__btn${theme === value ? ' is-on' : ''}`}
                onClick={() => chooseTheme(value)}
              >
                <Icon name={icon} size={14} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Privacy & data */}
        {onOpenDeleteMe && (
          <section className="settings-panel__section">
            <div className="settings-panel__label">Privacy &amp; data</div>
            <div className="settings-panel__row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.5 }}>
                Erase your conversations and saved prompts. You'll start fresh next time you sign in.
              </div>
              <button
                type="button"
                className="settings-panel__delete-me"
                onClick={onOpenDeleteMe}
              >
                Delete my account &amp; history
              </button>
            </div>
          </section>
        )}
      </div>

      <ConfirmDialog
        open={confirmUnlink}
        onOpenChange={setConfirmUnlink}
        title="Unlink Chronos?"
        description="Kairos will stop using your Chronos data until you link again."
        confirmLabel="Unlink"
        destructive
        onConfirm={doUnlink}
      />
    </>
  );
}
